import { useRef, useState } from "react";
import { Plus, Trash2, ListChecks } from "lucide-react";
import type { ActionItem, Priority } from "../types";
import { cn } from "../lib/utils";
import styles from "./ActionItemEditor.module.css";

interface ActionItemEditorProps {
  items: ActionItem[];
  onChange: (items: ActionItem[]) => void;
}

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: "high", label: "High" },
  { value: "medium", label: "Med" },
  { value: "low", label: "Low" },
];

/**
 * Checklist-style editor for action items.
 * Each item has: task, priority. Add/remove supported.
 */
export function ActionItemEditor({ items, onChange }: ActionItemEditorProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [newPriority, setNewPriority] = useState<Priority>("medium");
  const taskInputRef = useRef<HTMLInputElement>(null);

  function handleAdd() {
    const trimmed = newTask.trim();
    if (!trimmed) return;
    onChange([
      ...items,
      {
        task: trimmed,
        owner: null,
        deadline_mentioned: null,
        priority: newPriority,
      },
    ]);
    setNewTask("");
    setNewPriority("medium");
    setShowAddForm(false);
  }

  function handleRemove(idx: number) {
    onChange(items.filter((_, i) => i !== idx));
  }

  function handleFieldChange(
    idx: number,
    key: keyof ActionItem,
    value: string
  ) {
    onChange(
      items.map((item, i) => (i === idx ? { ...item, [key]: value } : item))
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h3 className={styles.label}>
          <ListChecks className="h-4 w-4" />
          Action Items
        </h3>
        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            setShowAddForm(true);
            requestAnimationFrame(() => taskInputRef.current?.focus());
          }}
          aria-label="Add action item"
        >
          <Plus className="h-4 w-4" />
          <span>Add</span>
        </button>
      </div>

      {items.length === 0 && !showAddForm && (
        <p className={styles.empty}>No action items. Tap "Add" to create one.</p>
      )}

      <ul className={styles.list}>
        {items.map((item, idx) => (
          <li key={idx} className={styles.item}>
            <div className={styles.itemTop}>
              <input
                type="text"
                className={styles.taskInput}
                value={item.task}
                onChange={(e) =>
                  handleFieldChange(idx, "task", e.target.value)
                }
                placeholder="Task description"
                aria-label="Action item task"
              />
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => handleRemove(idx)}
                aria-label={`Remove action item: ${item.task}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className={styles.itemMeta}>
              {/* Priority selector */}
              <div className={styles.priorityToggle}>
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(
                      styles.priorityBtn,
                      item.priority === opt.value &&
                        styles[
                          `priority${opt.value.charAt(0).toUpperCase()}${opt.value.slice(1)}` as keyof typeof styles
                        ]
                    )}
                    onClick={() =>
                      handleFieldChange(idx, "priority", opt.value)
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {showAddForm && (
        <div className={styles.addForm}>
          <input
            ref={taskInputRef}
            type="text"
            className={styles.taskInput}
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="New action item..."
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            aria-label="New action item"
          />
          <div className={styles.addFormMeta}>
            <div className={styles.priorityToggle}>
              {PRIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={cn(
                    styles.priorityBtn,
                    newPriority === opt.value &&
                      styles[
                        `priority${opt.value.charAt(0).toUpperCase()}${opt.value.slice(1)}` as keyof typeof styles
                      ]
                  )}
                  onClick={() => setNewPriority(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.addFormActions}>
            <button
              type="button"
              className={styles.confirmAdd}
              onClick={handleAdd}
            >
              <Plus className="h-4 w-4" />
              Add Item
            </button>
            <button
              type="button"
              className={styles.cancelAdd}
              onClick={() => {
                setShowAddForm(false);
                setNewTask("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
